import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Download, Upload, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { exportStagedDataToCSV, parseReimportCSV, applyReimportData, ExportRow } from '../utils/exportStagedData';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface ExportReimportPanelProps {
  batchId: string;
  onDataUpdated?: () => void;
}

export const ExportReimportPanel = ({ batchId, onDataUpdated }: ExportReimportPanelProps) => {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [isReimporting, setIsReimporting] = useState(false);
  const [reimportFile, setReimportFile] = useState<File | null>(null);
  const [reimportPreview, setReimportPreview] = useState<ExportRow[] | null>(null);
  const [reimportErrors, setReimportErrors] = useState<string[]>([]);

  const handleExport = async () => {
    if (!batchId) {
      toast({
        title: 'Error',
        description: 'No batch ID available',
        variant: 'destructive',
      });
      return;
    }

    setIsExporting(true);
    try {
      const csvContent = await exportStagedDataToCSV(batchId, true);
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `onboarding-data-export-${batchId.substring(0, 8)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'Export successful',
        description: 'Data exported to CSV file. You can now edit it and re-import.',
      });
    } catch (error: any) {
      toast({
        title: 'Export failed',
        description: error.message || 'Failed to export data',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setReimportFile(file);
    setReimportPreview(null);
    setReimportErrors([]);

    try {
      const text = await file.text();
      const { rows, errors } = parseReimportCSV(text);
      
      if (errors.length > 0) {
        setReimportErrors(errors);
        toast({
          title: 'CSV parsing errors',
          description: `Found ${errors.length} error(s). Please check the file format.`,
          variant: 'destructive',
        });
      } else {
        setReimportPreview(rows.slice(0, 10)); // Preview first 10 rows
        toast({
          title: 'File parsed successfully',
          description: `Found ${rows.length} row(s) to process.`,
        });
      }
    } catch (error: any) {
      toast({
        title: 'File read error',
        description: error.message || 'Failed to read file',
        variant: 'destructive',
      });
    }
  };

  const handleReimport = async () => {
    if (!reimportFile || !batchId) return;

    setIsReimporting(true);
    try {
      const text = await reimportFile.text();
      const { rows, errors } = parseReimportCSV(text);

      if (errors.length > 0) {
        setReimportErrors(errors);
        toast({
          title: 'Cannot re-import',
          description: `Please fix ${errors.length} error(s) first.`,
          variant: 'destructive',
        });
        setIsReimporting(false);
        return;
      }

      const results = await applyReimportData(batchId, rows);

      toast({
        title: 'Re-import complete',
        description: `Updated ${results.updated} row(s), deleted ${results.deleted} row(s). ${results.errors.length > 0 ? `${results.errors.length} error(s) occurred.` : ''}`,
        variant: results.errors.length > 0 ? 'destructive' : 'default',
      });

      if (results.errors.length > 0) {
        setReimportErrors(results.errors);
      } else {
        setReimportFile(null);
        setReimportPreview(null);
        setReimportErrors([]);
        onDataUpdated?.();
      }
    } catch (error: any) {
      toast({
        title: 'Re-import failed',
        description: error.message || 'Failed to re-import data',
        variant: 'destructive',
      });
    } finally {
      setIsReimporting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Export / Re-import Data</CardTitle>
        <CardDescription>
          Export your data, make corrections, and re-import with actions (EDIT/DELETE/UPDATE)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Export Section */}
        <div className="space-y-2">
          <Button
            onClick={handleExport}
            disabled={isExporting || !batchId}
            className="w-full"
          >
            {isExporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Export to CSV
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground">
            Exports all imported rows with onboarding_task_id and an Action column for corrections.
          </p>
        </div>

        {/* Re-import Section */}
        <div className="space-y-2 border-t pt-4">
          <Label htmlFor="reimport-file">Re-import CSV File</Label>
          <Input
            id="reimport-file"
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            disabled={isReimporting}
          />
          <p className="text-xs text-muted-foreground">
            Select the exported CSV file after making corrections. Use the Action column:
            <br />
            • <strong>EDIT</strong> or <strong>UPDATE</strong>: Update the row
            <br />
            • <strong>DELETE</strong>: Mark row for deletion
            <br />
            • <strong>Empty</strong>: No change
          </p>

          {reimportPreview && reimportPreview.length > 0 && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                Preview: {reimportPreview.length} row(s) ready to process. 
                {reimportPreview.some(r => r.action) && (
                  <span className="block mt-1">
                    Actions found: {reimportPreview.filter(r => r.action).map(r => r.action).join(', ')}
                  </span>
                )}
              </AlertDescription>
            </Alert>
          )}

          {reimportErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-1">
                  <strong>Errors found:</strong>
                  {reimportErrors.slice(0, 5).map((error, idx) => (
                    <div key={idx} className="text-xs">{error}</div>
                  ))}
                  {reimportErrors.length > 5 && (
                    <div className="text-xs">... and {reimportErrors.length - 5} more</div>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {reimportFile && (
            <Button
              onClick={handleReimport}
              disabled={isReimporting || reimportErrors.length > 0}
              className="w-full"
            >
              {isReimporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Apply Re-import
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

